import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import './style.scss';

import BaseButton from '../../BaseButton/BaseButton';
import { posOptions } from '../shared/pos';
import { initialUserState } from '../shared/initialUserState';
import UserDetails from './../UserDetails/UserDetails';
import { formValidation, initialFormState } from './formSettings';

/**
 * Step 3 form component.
 */
export default function Step3({ initialValues, availableDivisions, submitting, error, onSubmit }): JSX.Element {
  const methods = useForm({
    resolver: yupResolver(formValidation),
    defaultValues: { ...initialFormState, ...initialValues },
    shouldFocusError: false,
  });

  const [userDetails, setUserDetails] = useState(initialValues.user);
  const [isSelectedPos, setSelectedPos] = useState(false);
  const values = methods.getValues();

  useEffect(() => {
    // Disabled submit button if Pos === Others.
    const subscription = methods.watch(value => {
      setSelectedPos(value.posSystem === posOptions[3].value);
    });

    return (): void => subscription.unsubscribe();
  }, [values.posSystem, methods.watch, methods]);

  const handleFormSubmit = (): void => {
    methods.setValue('user', userDetails);
    onSubmit(methods.getValues());
  };

  const handleFormChange = (): void => {
    methods.setValue('user', userDetails);
  };

  const handleEnrollAnotherStore = (): void => {
    setUserDetails([
      ...userDetails,
      {
        ...initialUserState,
        plan: initialValues.user[0].plan,
        addon: initialValues.user[0].addon,
        firstName: initialValues.user[0].firstName,
        lastName: initialValues.user[0].lastName,
        phone: initialValues.user[0].phone,
        email: initialValues.user[0].email,
      },
    ]);
  };

  const handleRemoveAnotherStore = (index: number): void => {
    const updated = userDetails.filter((user, userIndex) => index !== userIndex);

    setUserDetails(updated);
    methods.setValue('user', userDetails);
  };

  const isDisabledSubmit = isSelectedPos || submitting;

  return (
    <FormProvider {...methods}>
      <h2 className="text-center">C-Store Essentials</h2>
      <div className="form-notes">
        <p>
          <span className="red">*</span>These fields are mandatory to complete.
        </p>
      </div>
      <form
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        onChange={handleFormChange}
        className="cse-step3"
        noValidate
      >
        <UserDetails
          availableDivisions={availableDivisions}
          onEnrollAnotherStore={handleEnrollAnotherStore}
          onRemoveEnrollAnotherStore={handleRemoveAnotherStore}
          data={userDetails}
          serverErrors={error}
          hasPasswordFields
        />

        <div className="row">
          <div className="col-md-12">
            <div className="select-field">
              <div className="checkbox-with-mark">
                <label className={classNames('checkbox', { invalid: methods.formState.errors.iHavePC })}>
                  <span className="required-mark">*</span>
                  <input {...methods.register('iHavePC')} type="checkbox" />I have a stationary windows PC on site that
                  is connected to my back office
                </label>
              </div>
            </div>
          </div>
        </div>

        <BaseButton
          className="btn btn-orange btn-submit btn-lg btn-block"
          type="submit"
          disabled={isDisabledSubmit}
          loading={submitting}
        >
          Next
        </BaseButton>
      </form>
    </FormProvider>
  );
}
